import React, { HTMLAttributes, useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/outline';
import { JoveNumberInput } from '@jove-frontend/mui';
import { Check, Close, EditOutlined } from '@mui/icons-material';
import { IconButton, TextField, styled } from '@mui/material';
import { Tooltip } from '@mui/material';
import clsx from 'clsx';

export enum TableFilterCardItemEditTypeEnum {
  Normal = 'normal',
  Count = 'count',
}

export interface TableFilterCardItemFieldProp {
  key?: string;
  value?: string;
  title?: string;
  content?: string;
  isSelected?: boolean;
  canEdit?: boolean;
  editType?: TableFilterCardItemEditTypeEnum;
  filterValue?: string;
  description?: string;
}

type TableFilterCardItemViewProps = HTMLAttributes<HTMLDivElement> & {
  info: TableFilterCardItemFieldProp;
  index: number;
  titleKey: string;
  isSelectedKey: string;
  clickItem?: (index: number, item: TableFilterCardItemFieldProp) => void;
  confirmEdit?: (index: number, item: TableFilterCardItemFieldProp) => void;
  disabled?: boolean;
  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void;
};

export const StyleTextField = styled(TextField)({
  '& textarea:focus': {
    'box-shadow': 'none',
  },
  '& input:focus': {
    'box-shadow': 'none',
  },
});

const TableFilterCardItemView = (props: TableFilterCardItemViewProps) => {
  const { className, info, index, titleKey, isSelectedKey, clickItem, confirmEdit, disabled, onMouseEnter, onMouseLeave } = props;
  const [itemEditType, setItemEditType] = useState(TableFilterCardItemEditTypeEnum.Normal);
  const [countValue, setCountValue] = useState(info?.filterValue || '');

  /**
   * methods
   */
  const clickAction = () => {
    if (!disabled && itemEditType === TableFilterCardItemEditTypeEnum.Normal) {
      clickItem?.(index, { ...info, isSelected: !info.isSelected });
    }
  };

  const editAction = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (info?.editType) setItemEditType(info?.editType);
  };

  const cancelEditAction = () => {
    setItemEditType(TableFilterCardItemEditTypeEnum.Normal);
  };

  const confirmAction = () => {
    confirmEdit?.(index, {
      ...info,
      filterValue: countValue,
      isSelected: true,
    });
    cancelEditAction();
  };

  const countChangeAction = (value) => {
    setCountValue(value);
  };

  return (
    <div
      className={clsx(
        'relative mr-1 flex min-w-[100px] flex-col rounded-lg border-2 px-3 py-2',
        `${info[isSelectedKey] ? 'border-brand-secondary text-brand-secondary' : 'border-gray-info text-black-light'}`,
        `${itemEditType !== TableFilterCardItemEditTypeEnum.Normal ? 'justify-center' : ''}`,
        {
          'cursor-pointer': !disabled,
        },
        className
      )}
      onClick={clickAction}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {info?.canEdit && itemEditType === TableFilterCardItemEditTypeEnum.Normal && (
        <IconButton
          className="absolute -right-4 -top-4"
          size="small"
          aria-label="edit"
          disabled={disabled}
          onClick={editAction}
          style={info[isSelectedKey] ? { color: '#0E35D1' } : null}
        >
          <EditOutlined />
        </IconButton>
      )}
      {itemEditType === TableFilterCardItemEditTypeEnum.Normal && (
        <div className="flex flex-1 flex-col">
          <div className="flex flex-1 flex-row">
            <p className="text-sm font-medium">
              {info[titleKey]}
              {info.isSelected && info?.editType === TableFilterCardItemEditTypeEnum.Count ? (
                <span className="font-bold">{` >= ${info?.filterValue || 1}`}</span>
              ) : null}
            </p>
            {info.description && (
              <Tooltip title={info.description} placement="top">
                <InformationCircleIcon className="ml-1 h-5 w-5" />
              </Tooltip>
            )}
          </div>
          <span className="text-base font-bold">{info.content}</span>
        </div>
      )}
      {itemEditType === TableFilterCardItemEditTypeEnum.Count && (
        <div className="flex flex-row items-center">
          <JoveNumberInput
            className="mt-1"
            label={info[titleKey]}
            value={countValue}
            valueChange={countChangeAction}
            prefixText=">="
            min={1}
            sx={{ minWidth: '100px', width: '100px' }}
          />
          <IconButton size="small" aria-label="cancel" onClick={cancelEditAction}>
            <Close />
          </IconButton>
          <IconButton size="small" aria-label="confirm" onClick={confirmAction}>
            <Check />
          </IconButton>
        </div>
      )}
    </div>
  );
};

export default TableFilterCardItemView;
